/* eslint-disable no-unused-vars */
import { useState, useEffect, useMemo } from "react";

import { C, EMP_MAP, TODAY } from "../../../../constants";
import { daysUntil, getInit } from "../../../../utils";
import apiClient from "../../../../services/api";
import { mapConsultant } from "../../../../mappers";
import { useAuth } from "../../../../app/providers/AuthProvider";
import { useSystemSettings } from "../../../../app/providers/SystemSettingsProvider";
import {
  Button,
  DialogPrimitives,
  FieldGroup,
  FieldLabel,
  Input,
  Select,
  SheetPrimitives,
  TooltipPrimitives,
} from "../../../../shared/ui";

function ResourceIntelligenceTab({ consultants, showToast, KpiCard, C }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matchSkills, setMatchSkills] = useState("");
  const [matchHours, setMatchHours] = useState("160");
  const [matchBudget, setMatchBudget] = useState("");
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiClient.getResourceIntelligence().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleMatch() {
    setMatchLoading(true);
    try {
      const r = await apiClient.matchConsultantsToProject({ requiredSkills: matchSkills.split(",").map(s => s.trim()).filter(Boolean), requiredHours: Number(matchHours) || 160, budgetPerHour: Number(matchBudget) || undefined });
      setMatchResults(r);
    } catch (e) { showToast?.(e.message || "Match failed", "error"); }
    finally { setMatchLoading(false); }
  }

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#8fa4c8" }}>Loading intelligence data...</div>;
  if (!data) return <div style={{ textAlign: "center", padding: 40, color: "#8fa4c8" }}>No data available</div>;

  const { forecast, profitSummary } = data;
  const colors = ["#3b7ffa", "#10c981", "#f59e0b", "#a855f7", "#ef4444"];

  return (
    <div className="pg-body">
      {/* KPI strip */}
      <div className="krow">
        <KpiCard label="Total Revenue (YTD)" value={`${(profitSummary.totalRevenue / 1000).toFixed(0)}K kr`} accent="#10c981" />
        <KpiCard label="Total Profit (YTD)" value={`${(profitSummary.totalProfit / 1000).toFixed(0)}K kr`} accent={profitSummary.totalProfit > 0 ? "#10c981" : "#ef4444"} />
        <KpiCard label="Avg Margin" value={`${Math.round(profitSummary.avgMargin * 100)}%`} accent="#3b7ffa" />
        <KpiCard label="Billable Consultants" value={profitSummary.consultantCount} />
      </div>

      {/* Allocation Heatmap */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#4a6a96", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 12 }}>Per-Consultant Project Allocation</div>
        <div className="panel" style={{ overflowX: "auto" }}>
          {forecast.map(c => {
            const totalHrs = c.currentProjects.reduce((s, p) => s + p.loggedHours, 0);
            return (
              <div key={c.consultantId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: "1px solid #14213d" }}>
                <div style={{ width: 140, fontSize: 12, fontWeight: 600, color: "#dce8f8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                <div style={{ flex: 1, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  {c.currentProjects.length === 0 && (c.plannedTotalPct || 0) === 0 && <span style={{ fontSize: 11, color: "#a855f7" }}>Bench</span>}
                  {c.currentProjects.slice(0, 3).map((p, i) => {
                    const pct = totalHrs > 0 ? Math.round((p.loggedHours / totalHrs) * 100) : 0;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ width: Math.max(30, pct * 1.2), height: 14, background: colors[i % colors.length], borderRadius: 3 }} />
                        <span style={{ fontSize: 9, color: "#8fa4c8", whiteSpace: "nowrap" }}>{p.clientName || p.name} {pct}%</span>
                      </div>
                    );
                  })}
                  {c.currentProjects.length > 3 && <span style={{ fontSize: 9, color: "#4a6a96" }}>+{c.currentProjects.length - 3}</span>}
                </div>
                <div style={{ width: 120, textAlign: "right", fontSize: 11, color: "#8fa4c8", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  {(c.plannedTotalPct || 0) > 0 && (
                    <span title="Planned allocation from assignments" style={{ color: "#a855f7" }}>
                      planned {c.plannedTotalPct}%
                    </span>
                  )}
                  <span title="Logged utilisation (YTD)" style={{ color: "#8fa4c8" }}>
                    logged {c.currentUtilPct}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Capacity Forecast */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#4a6a96", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 12 }}>Capacity Forecast (Next 3 Months)</div>
        <div className="panel" style={{ overflowX: "auto" }}>
          <table className="tbl" style={{ minWidth: 700 }}>
            <thead><tr><th>Consultant</th><th>Type</th>{forecast[0]?.forecast.map(m => <th key={m.label}>{m.label}</th>)}</tr></thead>
            <tbody>
              {forecast.map(c => (
                <tr key={c.consultantId}>
                  <td style={{ fontWeight: 600, color: "#dce8f8", fontSize: 12 }}>{c.name}</td>
                  <td style={{ fontSize: 11, color: "#8fa4c8" }}>{c.employmentType}</td>
                  {c.forecast.map(m => {
                    const bg = m.utilizationPct >= 90 ? "#ef4444" : m.utilizationPct >= 70 ? "#f59e0b" : m.utilizationPct >= 40 ? "#3b82f6" : "#10c981";
                    return (
                      <td key={m.label}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: "#14213d", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(m.utilizationPct, 100)}%`, height: "100%", background: bg, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 10, color: "#8fa4c8", minWidth: 40, textAlign: "right" }}>{m.availableHours}h</span>
                        </div>
                        {m.vacationDays > 0 && <div style={{ fontSize: 9, color: "#f59e0b", marginTop: 2 }}>{m.vacationDays}d vac</div>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Auto-Matching */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#4a6a96", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 12 }}>Auto-Match Consultant to Project</div>
        <div className="panel" style={{ padding: 16 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <input className="finput" placeholder="Required skills (comma-separated)" value={matchSkills} onChange={e => setMatchSkills(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
            <input className="finput" type="number" placeholder="Hours/month" value={matchHours} onChange={e => setMatchHours(e.target.value)} style={{ width: 120 }} />
            <input className="finput" type="number" placeholder="Budget kr/hr" value={matchBudget} onChange={e => setMatchBudget(e.target.value)} style={{ width: 120 }} />
            <button className="wr-gen-btn aud-cta-btn" onClick={handleMatch} disabled={matchLoading} style={{ fontSize: 12, padding: "7px 16px" }}>{matchLoading ? "Matching..." : "Find Best Match"}</button>
          </div>
          {matchResults && matchResults.slice(0, 10).map((r, i) => (
            <div key={r.consultantId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid #14213d" }}>
              <div style={{ width: 24, height: 24, borderRadius: 12, background: r.matchScore >= 70 ? "#10c981" : r.matchScore >= 40 ? "#f59e0b" : "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff" }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#dce8f8" }}>{r.consultantName}</div>
                <div style={{ fontSize: 10, color: "#8fa4c8", marginTop: 2 }}>Skill: {r.skillScore}% | Avail: {r.availabilityScore}% | Util: {r.utilisationScore}% | Rate: {r.rateScore}%</div>
                {r.matchedSkills.length > 0 && <div style={{ fontSize: 10, color: "#10c981", marginTop: 2 }}>Matched: {r.matchedSkills.join(", ")}</div>}
                {r.missingSkills.length > 0 && <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 2 }}>Missing: {r.missingSkills.join(", ")}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: r.matchScore >= 70 ? "#10c981" : r.matchScore >= 40 ? "#f59e0b" : "#ef4444" }}>{r.matchScore}%</div>
                <div style={{ fontSize: 10, color: "#8fa4c8" }}>{r.billRate} kr/hr | {r.availableHours}h free</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Profit Per Consultant */}
      <div style={{ marginTop: 24, marginBottom: 24 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#4a6a96", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 12 }}>Profit Per Consultant (YTD)</div>
        <div className="panel" style={{ overflowX: "auto" }}>
          <table className="tbl" style={{ minWidth: 800 }}>
            <thead><tr><th>Consultant</th><th>Bill Rate</th><th>Cost Rate</th><th>Hours/mo</th><th>Revenue</th><th>Cost</th><th>Profit</th><th>Margin</th></tr></thead>
            <tbody>
              {profitSummary.consultants.sort((a, b) => b.ytdProfit - a.ytdProfit).map(c => (
                <tr key={c.consultantId}>
                  <td style={{ fontWeight: 600, color: "#dce8f8", fontSize: 12 }}>{c.consultantName}</td>
                  <td style={{ fontSize: 12, color: "#8fa4c8" }}>{c.billRate} kr</td>
                  <td style={{ fontSize: 12, color: "#8fa4c8" }}>{c.costRate > 0 ? `${c.costRate} kr` : "—"}</td>
                  <td style={{ fontSize: 12, color: "#8fa4c8" }}>{c.billableHours}h</td>
                  <td style={{ fontSize: 12, color: "#10c981" }}>{(c.ytdRevenue / 1000).toFixed(0)}K</td>
                  <td style={{ fontSize: 12, color: "#ef4444" }}>{c.costRate > 0 ? `${(c.ytdCost / 1000).toFixed(0)}K` : "—"}</td>
                  <td style={{ fontSize: 12, fontWeight: 600, color: c.ytdProfit >= 0 ? "#10c981" : "#ef4444" }}>{(c.ytdProfit / 1000).toFixed(0)}K</td>
                  <td style={{ fontSize: 12, fontWeight: 600, color: c.ytdMargin >= 0.3 ? "#10c981" : c.ytdMargin >= 0.15 ? "#f59e0b" : "#ef4444" }}>{Math.round(c.ytdMargin * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function ResourcesPage({
  consultants,
  setConsultants,
  deals,
  nav,
  showToast,
  KpiCardComponent,
  AvatarComponent,
}) {
  const KpiCard = KpiCardComponent;
  const Avt = AvatarComponent;
  const { isSuperAdmin } = useAuth();
  const { assignmentSource } = useSystemSettings();
  const trackerOwnsAssignments = assignmentSource === "TRACKER";
  const [statusF,setStatusF]=useState("All");
  const [empTypeF,setEmpTypeF]=useState("All");
  const [search,setSearch]=useState("");
  const [sortCol,setSortCol]=useState("name");
  const [sortDir,setSortDir]=useState(1);
  const [selectedResId,setSelectedResId]=useState(null);
  const [syncing,setSyncing]=useState(false);
  const [showNewModal,setShowNewModal]=useState(false);
  const [newForm,setNewForm]=useState({ name:"", role:"", skills:"", billRate:"", costRate:"", salary:"", employmentType:"FULL_TIME" });
  const [savingNew,setSavingNew]=useState(false);
  const [editingSkills,setEditingSkills]=useState(null); // consultant id being edited
  const [skillDraft,setSkillDraft]=useState("");

  async function handleSync() {
    setSyncing(true);
    try {
      const result = await apiClient.syncConsultantsFromTracker();
      showToast?.(`Synced ${result.created} new, ${result.updated} updated from tracker`, "success");
      const fresh = await apiClient.getConsultants();
      setConsultants(fresh.map(mapConsultant));
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Sync failed", "error");
    } finally { setSyncing(false); }
  }

  async function handleCreateConsultant() {
    if (!newForm.name.trim()) return;
    setSavingNew(true);
    try {
      await apiClient.createConsultant({
        name: newForm.name.trim(),
        role: newForm.role.trim() || "Consultant",
        skills: newForm.skills ? newForm.skills.split(",").map(s=>s.trim()).filter(Boolean) : [],
        billRate: Number(newForm.billRate) || 0,
        costRate: Number(newForm.costRate) || 0,
        salary: Number(newForm.salary) || 0,
        employmentType: newForm.employmentType,
        status: "BENCH",
      });
      const fresh = await apiClient.getConsultants();
      setConsultants(fresh.map(mapConsultant));
      setNewForm({ name:"", role:"", skills:"", billRate:"", costRate:"", salary:"", employmentType:"FULL_TIME" });
      setShowNewModal(false);
      showToast?.("Consultant created", "success");
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Create failed", "error");
    } finally { setSavingNew(false); }
  }

  async function handleSkillsSave(consultantId, skillsArr) {
    try {
      await apiClient.updateConsultant(consultantId, { skills: skillsArr });
      const fresh = await apiClient.getConsultants();
      setConsultants(fresh.map(mapConsultant));
      setEditingSkills(null);
      setSkillDraft("");
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Skill update failed", "error");
    }
  }

  async function handleFieldSave(consultantId, field, value) {
    try {
      await apiClient.updateConsultant(consultantId, { [field]: value });
      const fresh = await apiClient.getConsultants();
      setConsultants(fresh.map(mapConsultant));
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Update failed", "error");
    }
  }

  const [tab, setTab] = useState("resources");
  // Exclude admin users and resigned consultants (deterministic isAdmin flag).
  // `visibleConsultants` still includes inactive-but-not-resigned rows so the
  // drawer can open them; the Inactive section and KPI counters branch off
  // this below.
  const visibleConsultants = consultants.filter(c => !c.isAdmin && c.status !== "Resigned");
  const _todayMs = Date.now();
  const isInactiveNow = (c) => {
    if (!c?.inactiveSince) return false;
    const t = new Date(c.inactiveSince).getTime();
    return isFinite(t) && t <= _todayMs;
  };
  // Active = not yet past the inactiveSince cutoff (or no cutoff set).
  // KPIs and the billable/deployed/bench counts track this list; the
  // Inactive section below shows the rest as an audit trail.
  const activeConsultants = visibleConsultants.filter(c => !isInactiveNow(c));
  const inactiveConsultants = visibleConsultants
    .filter(isInactiveNow)
    .sort((a, b) => new Date(b.inactiveSince) - new Date(a.inactiveSince));
  // Drawer selection tracked by id — derive the live consultant from the fresh list
  // so edits don't detach the drawer from the array and cause a refresh loop.
  const selectedRes = useMemo(
    () => consultants.find(c => c.id === selectedResId) || null,
    [consultants, selectedResId]
  );

  const totalC=activeConsultants.length;
  // Interns don't count toward utilization or billing
  const billableConsultants = activeConsultants.filter(c => !["Intern","INTERN","intern"].includes(c.employmentType));
  const deployed=billableConsultants.filter(c=>c.status==="Deployed").length;
  const bench=billableConsultants.filter(c=>c.status==="Bench"||c.status==="Partial").length;
  const rollingOff=activeConsultants.filter(c=>c.availableFrom&&daysUntil(c.availableFrom)<=30&&daysUntil(c.availableFrom)>=0).length;
  function toggleSort(col){if(sortCol===col)setSortDir(d=>-d);else{setSortCol(col);setSortDir(1);}}
  function Th({col,children}){const a=sortCol===col;return <th className={a?"sorted":""} onClick={()=>toggleSort(col)}>{children}{a?(sortDir===1?" ↑":" ↓"):""}</th>;}

  const rows=[...activeConsultants]
    .filter(c=>{
      if(statusF!=="All"&&c.status!==statusF)return false;
      if(empTypeF!=="All"&&c.employmentType!==empTypeF)return false;
      if(search&&!c.name.toLowerCase().includes(search.toLowerCase())&&!(c.role||"").toLowerCase().includes(search.toLowerCase())&&!(c.skills||[]).some(s=>s.toLowerCase().includes(search.toLowerCase())))return false;
      return true;
    })
    .sort((a,b)=>{const av=a[sortCol],bv=b[sortCol];return(typeof av==="number"?(av||0)-(bv||0):String(av||"").localeCompare(String(bv||"")))*sortDir;});

  // Group into sections
  const fulltime = rows.filter(c => ["Full Time","FULL_TIME","Full_time","Fulltime"].includes(c.employmentType));
  const talentHub = rows.filter(c => ["Talent Hub","TALENT_HUB","Talent_hub"].includes(c.employmentType));
  const interns = rows.filter(c => ["Intern","INTERN","intern"].includes(c.employmentType));
  const other = rows.filter(c => !fulltime.includes(c) && !talentHub.includes(c) && !interns.includes(c));

  const sections = [
    { key: "fulltime", title: "Full Time", data: fulltime, accent: "#3b7ffa" },
    { key: "talenthub", title: "Talent Hub", data: talentHub, accent: "#f59e0b" },
    { key: "interns", title: "Interns", data: interns, accent: "#a855f7" },
    ...(other.length > 0 ? [{ key: "other", title: "Other", data: other, accent: "#8fa4c8" }] : []),
  ].filter(s => s.data.length > 0);

  // Demand vs supply
  const unassigned=(deals||[]).filter(d=>!d.assignedConsultant);
  const available=activeConsultants.filter(c=>c.status==="Bench"||c.status==="Partial");
  const demandMatches=unassigned.map(d=>{
    const match=available.find(c=>(c.skills||[]).some(s=>(d.requiredRoleOrSkill||"").toLowerCase().includes(s.toLowerCase())||s.toLowerCase().includes((d.requiredRoleOrSkill||"").split(" ")[0].toLowerCase())));
    return {deal:d,match};
  });

  const statusStyle={Deployed:{fg:C.blue,bg:C.blueB},Bench:{fg:C.purple,bg:C.purpleB},Partial:{fg:C.yellow,bg:C.yellowB}};

  // Helper: compute average utilisation from historicalUtil
  function avgUtil(c){
    const hu=c.historicalUtil;
    if(!hu)return c.utilizationPct;
    const years=Object.keys(hu);
    const allAvgs=years.map(y=>hu[y].avg).filter(v=>v>0);
    return allAvgs.length?Math.round(allAvgs.reduce((a,b)=>a+b,0)/allAvgs.length):c.utilizationPct;
  }

  // Resource detail drawer
  function ResDrawer(){
    if(!selectedRes)return null;
    const c=selectedRes;
    const ss=statusStyle[c.status]||statusStyle.Deployed;
    const hu=c.historicalUtil||{};
    const avgU=avgUtil(c);
    const vacRemaining=(c.vacationDaysTotal||25)-(c.vacationDaysUsed||0);
    const tenure=c.joinDate?Math.round((new Date("2026-03-26")-new Date(c.joinDate))/(1000*60*60*24*30.44))+"m":"—";
    const MO_LABELS=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const MO_KEYS=["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

    return (
      <SheetPrimitives.Sheet open={!!c} onOpenChange={(open) => !open && setSelectedResId(null)}>
        <SheetPrimitives.SheetContent side="right">
          {/* Header — avatar + name + role + status chips */}
          <SheetPrimitives.SheetHeader>
            <div className="flex items-start gap-3.5">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold text-white shrink-0"
                style={{ background: ss.fg }}
              >
                {getInit(c.name)}
              </div>
              <div className="flex-1 min-w-0">
                <SheetPrimitives.SheetTitle>{c.name}</SheetPrimitives.SheetTitle>
                <div className="mt-0.5 text-[13px] text-rc-blue-100">{c.role}</div>
                <div className="mt-1.5 flex gap-1.5 flex-wrap">
                  <span className="chip" style={{color:ss.fg,background:ss.bg,border:`1px solid ${ss.fg}22`,fontSize:10}}><span className="dot" style={{background:ss.fg}}/>{c.status}</span>
                  <span className="chip" style={{color:EMP_MAP[c.employmentType]?.fg||C.dim,background:EMP_MAP[c.employmentType]?.bg||"transparent",border:`1px solid ${EMP_MAP[c.employmentType]?.fg||C.dim}22`,fontSize:10}}>{c.employmentType}</span>
                </div>
              </div>
            </div>
          </SheetPrimitives.SheetHeader>

          <SheetPrimitives.SheetBody>
            {/* Key metrics — editable */}
            <div className="mt-[22px]">
              <div className="flex items-center justify-between gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">
                <span>Overview</span>
                <button onClick={()=>setEditingSkills(editingSkills==="overview-"+c.id?null:"overview-"+c.id)}
                  style={{background:"none",border:"1px solid #14213d",borderRadius:5,padding:"2px 10px",color:"#60a5fa",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
                  {editingSkills==="overview-"+c.id?"Done":"✎ Edit"}
                </button>
              </div>
              {editingSkills==="overview-"+c.id ? (
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    ["salary","Salary (kr/mo)","number",c.salary],
                    ["billRate","Bill Rate (kr/hr)","number",c.billRate],
                    ["costRate","Cost Rate (kr/hr)","number",c.costRate],
                    ["dailyCapacity","Daily Capacity (hrs)","number",c.dailyCapacity||8],
                    ["vacationDaysTotal","Vacation Days","number",c.vacationDaysTotal||25],
                    ["vacationDaysUsed","Vacation Used","number",c.vacationDaysUsed||0],
                  ].map(([field,label,type,val])=>(
                    <div key={field} className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                      <div className="mb-1 text-[9.5px] uppercase tracking-[0.03em] text-[#4a6a96]">{label}</div>
                      <input type={type} defaultValue={val} style={{width:"100%",padding:"4px 8px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:13,fontFamily:"inherit",fontWeight:700,outline:"none"}}
                        onBlur={e=>{const v=Number(e.target.value);if(v!==val)handleFieldSave(c.id,field,v);}}
                        onKeyDown={e=>{if(e.key==="Enter"){const v=Number(e.target.value);if(v!==val)handleFieldSave(c.id,field,v);}}}/>
                    </div>
                  ))}
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                    <div className="rs-lbl">Employment Type</div>
                    <select defaultValue={c.employmentType==="Full Time"?"FULL_TIME":c.employmentType==="Talent Hub"?"TALENT_HUB":c.employmentType==="Intern"?"INTERN":c.employmentType}
                      style={{width:"100%",padding:"4px 8px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:12,fontFamily:"inherit",outline:"none"}}
                      onChange={e=>handleFieldSave(c.id,"employmentType",e.target.value)}>
                      <option value="FULL_TIME">Full Time</option>
                      <option value="TALENT_HUB">Talent Hub</option>
                      <option value="INTERN">Intern</option>
                    </select>
                  </div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                    <div className="rs-lbl">Role</div>
                    <input defaultValue={c.role} style={{width:"100%",padding:"4px 8px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:13,fontFamily:"inherit",outline:"none"}}
                      onBlur={e=>{if(e.target.value!==c.role)handleFieldSave(c.id,"role",e.target.value);}}/>
                  </div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                    <div className="rs-lbl">Name</div>
                    <input defaultValue={c.name} style={{width:"100%",padding:"4px 8px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:13,fontFamily:"inherit",outline:"none"}}
                      onBlur={e=>{if(e.target.value!==c.name)handleFieldSave(c.id,"name",e.target.value);}}/>
                  </div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                    <div className="rs-lbl">Available From</div>
                    <input type="date" min={new Date().toISOString().slice(0,10)} defaultValue={c.availableFrom?String(c.availableFrom).slice(0,10):""} style={{width:"100%",padding:"4px 8px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:12,fontFamily:"inherit",outline:"none",colorScheme:"dark"}}
                      onBlur={e=>{const v=e.target.value;handleFieldSave(c.id,"availableFrom",v||null);}}/>
                  </div>
                  <div className="col-span-full bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                    <div className="rs-lbl">Notes</div>
                    <textarea defaultValue={c.notes||""} rows={2} placeholder="Add notes…" style={{width:"100%",padding:"6px 8px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:12,fontFamily:"inherit",outline:"none",resize:"vertical"}}
                      onBlur={e=>{if(e.target.value!==(c.notes||""))handleFieldSave(c.id,"notes",e.target.value||null);}}/>
                  </div>
                  {/* Lifecycle — effective-dated retirement. Setting a date
                      here excludes this consultant from utilisation, blended
                      rate, headcount, and tracker revenue rollups from that
                      date onward. Clear the date (or set to a future date)
                      to re-activate. */}
                  <div className="col-span-full bg-[#0e1c38] border border-[#14213d] border-t border-t-[#14213d] rounded-lg px-3 pt-2.5 pb-2.5 mt-1">
                    <div className="rs-lbl" style={{color: c.inactiveSince ? "#f59e0b" : undefined}}>
                      Inactive From {c.inactiveSince ? <span style={{fontSize:10,fontWeight:600,color:"#f59e0b",marginLeft:6}}>· EXCLUDED FROM CALCS</span> : <span style={{fontSize:10,fontWeight:400,color:"#6889b4",marginLeft:6}}>· leave blank to keep active</span>}
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center",marginTop:4}}>
                      <input type="date" defaultValue={c.inactiveSince||""} style={{flex:"0 0 180px",padding:"4px 8px",background:"#070e20",border:"1px solid "+(c.inactiveSince?"#f59e0b55":"#1c2e52"),borderRadius:6,color:"#dce8f8",fontSize:12,fontFamily:"inherit",outline:"none",colorScheme:"dark"}}
                        onBlur={e=>{const v=e.target.value;if(v!==(c.inactiveSince||""))handleFieldSave(c.id,"inactiveSince",v||null);}}/>
                      <input type="text" placeholder="Reason (optional)" defaultValue={c.inactiveReason||""} style={{flex:"1 1 auto",padding:"4px 8px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:12,fontFamily:"inherit",outline:"none"}}
                        onBlur={e=>{if(e.target.value!==(c.inactiveReason||""))handleFieldSave(c.id,"inactiveReason",e.target.value||null);}}/>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2.5">
                  {c.inactiveSince && (
                    <div className="col-span-full rounded-lg px-3 py-2.5 bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.3)]">
                      <div className="rs-lbl" style={{color:"#f59e0b"}}>⚠ Inactive from {c.inactiveSince}</div>
                      <div style={{fontSize:11,color:"#cbd5e1",marginTop:2}}>{c.inactiveReason ? c.inactiveReason : "Excluded from utilisation, rate, cost, and revenue rollups from this date onward."}</div>
                    </div>
                  )}
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Salary (kr/mo)</div><div className="rs-val">{c.salary?c.salary.toLocaleString():"—"}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Bill Rate</div><div className="rs-val blue">{c.billRate} kr/hr</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Cost Rate</div><div className="rs-val">{c.costRate} kr/hr</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Joined</div><div className="rs-val">{c.joinDate||"—"}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Tenure</div><div className="rs-val">{tenure}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Current Util</div><div className={`rs-val ${(c.currentUtilPct||0)>=80?"green":(c.currentUtilPct||0)>=50?"yellow":"blue"}`}>{c.currentUtilPct||0}%</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">YTD Util</div><div className={`rs-val ${(c.utilizationPct||0)>=80?"green":(c.utilizationPct||0)>=50?"yellow":"blue"}`}>{c.utilizationPct||0}%</div></div>
                  {(() => {
                    const billableHrs = Math.round((c.currentProjects||[]).reduce((s,p)=>s+(Number(p.loggedHours)||0),0));
                    const revenueYtd = Math.round(billableHrs * (Number(c.billRate)||0));
                    return (
                      <>
                        <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Total Billables (YTD)</div><div className="rs-val blue">{billableHrs.toLocaleString()} h</div></div>
                        <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Total Revenue (YTD)</div><div className="rs-val green">{revenueYtd.toLocaleString()} kr</div></div>
                      </>
                    );
                  })()}
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Daily Capacity</div><div className="rs-val">{c.dailyCapacity||8}h/day</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Vacation Left</div><div className={`rs-val ${vacRemaining<=5?"yellow":"green"}`}>{vacRemaining} / {c.vacationDaysTotal||25} days</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="rs-lbl">Available From</div><div className="rs-val">{c.availableFrom?String(c.availableFrom).slice(0,10):"Now"}</div></div>
                </div>
              )}
            </div>

            {/* Skills — editable */}
            <div className="mt-[22px]">
              <div className="flex items-center justify-between gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">
                <span>◇ Skills &amp; Expertise</span>
                {editingSkills!==c.id&&<button onClick={()=>{setEditingSkills(c.id);setSkillDraft(c.skills.join(", "));}} style={{background:"none",border:"1px solid #14213d",borderRadius:5,padding:"2px 10px",color:"#60a5fa",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>✎ Edit</button>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {c.skills.map(s=><span key={s} className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-[rgba(96,165,250,0.1)] text-[#60a5fa] border border-[rgba(96,165,250,0.15)]">{s}</span>)}
                {c.skills.length===0&&<span style={{fontSize:12,color:C.dim}}>No skills added yet — click Edit to add</span>}
              </div>
              {editingSkills===c.id&&(
                <div style={{marginTop:8,display:"flex",gap:6}}>
                  <input value={skillDraft} onChange={e=>setSkillDraft(e.target.value)} placeholder="React, Node.js, AWS, DevOps…" style={{flex:1,padding:"6px 10px",background:"#070e20",border:"1px solid #1c2e52",borderRadius:6,color:"#dce8f8",fontSize:12,fontFamily:"inherit",outline:"none"}}
                    onKeyDown={e=>{if(e.key==="Enter")handleSkillsSave(c.id,skillDraft.split(",").map(s=>s.trim()).filter(Boolean));}}/>
                  <button onClick={()=>handleSkillsSave(c.id,skillDraft.split(",").map(s=>s.trim()).filter(Boolean))} style={{padding:"6px 14px",background:"rgba(59,127,250,.14)",border:"1px solid rgba(59,127,250,.28)",borderRadius:6,color:"#60a5fa",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Save</button>
                  <button onClick={()=>{setEditingSkills(null);setSkillDraft("");}} style={{padding:"6px 10px",background:"none",border:"1px solid #14213d",borderRadius:6,color:"#8fa4c8",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </div>
              )}
            </div>

            {/* Historical utilisation chart */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">◎ Utilisation History</div>
              {Object.keys(hu).sort().map(year=>{
                const data=hu[year].monthly||[];
                return (
                  <div key={year} style={{marginBottom:16}}>
                    <div style={{fontSize:12,fontWeight:600,color:"#c4d4ee",marginBottom:6,display:"flex",alignItems:"center",gap:8}}>
                      {year}
                      <span style={{fontSize:10,color:"#4a6a96",fontWeight:400}}>Avg: {hu[year].avg}%</span>
                    </div>
                    <div className="flex items-end gap-[3px] h-20 py-2">
                      {data.map((val,i)=>{
                        const h=val>0?Math.max(4,(val/100)*70):2;
                        const bg=val===0?"#1c2e52":val<40?"#93c5fd":val<70?"#60a5fa":val<90?"#3b82f6":"#1e40af";
                        return (
                          <TooltipPrimitives.Tooltip key={i}>
                            <TooltipPrimitives.TooltipTrigger asChild>
                              <div className="flex-1 rounded-t min-w-0 transition-[filter,background] hover:brightness-125" style={{height:h,background:bg}} />
                            </TooltipPrimitives.TooltipTrigger>
                            <TooltipPrimitives.TooltipContent side="top">
                              {MO_LABELS[i]}: {val}%
                            </TooltipPrimitives.TooltipContent>
                          </TooltipPrimitives.Tooltip>
                        );
                      })}
                    </div>
                    <div className="flex gap-[3px] mt-1 [&>span]:flex-1 [&>span]:text-center [&>span]:text-[8px] [&>span]:text-[#4a6a96] [&>span]:min-w-0">
                      {MO_LABELS.map(m=><span key={m}>{m[0]}</span>)}
                    </div>
                  </div>
                );
              })}
              {/* 2026 current year from monthlyUtil */}
              {c.monthlyUtil&&!hu["2026"]&&(
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,color:"#c4d4ee",marginBottom:6,display:"flex",alignItems:"center",gap:8}}>
                    2026 <span style={{fontSize:10,color:"#4a6a96",fontWeight:400}}>(current)</span>
                  </div>
                  <div className="flex items-end gap-[3px] h-20 py-2">
                    {MO_KEYS.map((m,i)=>{
                      const val=c.monthlyUtil[m]||0;
                      const h=val>0?Math.max(4,(val/100)*70):2;
                      const bg=val===0?"#1c2e52":val<40?"#93c5fd":val<70?"#60a5fa":val<90?"#3b82f6":"#1e40af";
                      return (
                        <TooltipPrimitives.Tooltip key={m}>
                          <TooltipPrimitives.TooltipTrigger asChild>
                            <div className="flex-1 rounded-t min-w-0 transition-[filter,background] hover:brightness-125" style={{height:h,background:bg}} />
                          </TooltipPrimitives.TooltipTrigger>
                          <TooltipPrimitives.TooltipContent side="top">
                            {MO_LABELS[i]}: {val}%
                          </TooltipPrimitives.TooltipContent>
                        </TooltipPrimitives.Tooltip>
                      );
                    })}
                  </div>
                  <div className="flex gap-[3px] mt-1 [&>span]:flex-1 [&>span]:text-center [&>span]:text-[8px] [&>span]:text-[#4a6a96] [&>span]:min-w-0">
                    {MO_LABELS.map(m=><span key={m}>{m[0]}</span>)}
                  </div>
                </div>
              )}
            </div>

            {/* Billing rate history */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">◬ Billing Rate History</div>
              <div style={{borderRadius:8,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                <table className="w-full border-collapse text-[12px] [&_th]:text-left [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-[10px] [&_th]:text-[#4a6a96] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-[0.03em] [&_th]:border-b [&_th]:border-[#14213d] [&_td]:px-2 [&_td]:py-1.5 [&_td]:text-[#c4d4ee] [&_td]:border-b [&_td]:border-[#0e1930]">
                  <thead><tr><th>Year</th><th>Q1</th><th>Q2</th><th>Q3</th><th>Q4</th></tr></thead>
                  <tbody>
                    {(c.billingHistory||[]).map((bh,i)=>{
                      const prev=i>0?(c.billingHistory[i-1]):null;
                      function rateCell(q,prevQ){
                        const val=bh[q];
                        if(val===null||val===undefined)return <td style={{color:C.dim}}>—</td>;
                        const prevVal=prev?prev[q]:null;
                        const isUp=prevVal&&val>prevVal;
                        return <td className={isUp?"!text-[#10c981]":"!text-[#8fa4c8]"}>${val}{isUp&&<span style={{fontSize:9,marginLeft:3}}>↑</span>}</td>;
                      }
                      return <tr key={bh.year}><td style={{fontWeight:600}}>{bh.year}</td>{rateCell("q1")}{rateCell("q2")}{rateCell("q3")}{rateCell("q4")}</tr>;
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Active Assignments (from tracker) */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-2 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">
                <span>▣ Active Assignments</span>
                {trackerOwnsAssignments && (
                  <span
                    title="assignmentSource is TRACKER — edit in Tracker"
                    style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: ".08em",
                      textTransform: "uppercase", padding: "2px 8px", borderRadius: 10,
                      background: "rgba(143,164,200,.12)", color: C.sub,
                      border: `1px solid ${C.dim}33`,
                    }}
                  >Driven by Tracker</span>
                )}
              </div>
              {(c.currentProjects||[]).length === 0 && <div style={{fontSize:12,color:C.dim}}>No active project assignments</div>}
              {(c.currentProjects||[]).map((p,i) => (
                <div key={p.trackerProjectId||i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #0f1a30"}}>
                  <div style={{width:6,height:6,borderRadius:3,background:p.name==="Rainocode Internal"||p.name==="Raincode Internal"?"#4a6a96":"#10c981",flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#dce8f8"}}>{p.name}</div>
                    <div style={{fontSize:11,color:"#8fa4c8"}}>{p.clientName || "—"}{p.role ? ` · ${p.role}` : ""}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    {p.hourlyRate > 0 && <div style={{fontSize:12,fontWeight:600,color:"#10c981"}}>{Math.round(p.hourlyRate)} kr/hr</div>}
                    {p.startDate && <div style={{fontSize:10,color:"#4a6a96"}}>Since {p.startDate.slice(0,10)}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Project history */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">▣ Project History</div>
              {(c.projectHistory||[]).length===0&&<div style={{fontSize:12,color:C.dim}}>No project history yet</div>}
              {(c.projectHistory||[]).map((ph,i)=>(
                <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 bg-[#0e1c38] border border-[#14213d] rounded-lg mb-1.5 cursor-pointer transition-colors hover:border-[#1c2e52] hover:bg-[#111f3e]" onClick={()=>{if(ph.projectId)nav("project",ph.projectId);}}>
                  <div className="w-2 h-2 rounded-full shrink-0" style={{background:ph.status==="Active"?"#10c981":"#4a6a96"}}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#c4d4ee]">{ph.name}</div>
                    <div className="text-[11px] text-[#6889b4] mt-0.5">{ph.client} · {ph.role}</div>
                  </div>
                  <div className="text-[10px] text-[#4a6a96] text-right whitespace-nowrap">
                    <div style={{color:ph.status==="Active"?"#10c981":"#8fa4c8",fontWeight:500,fontSize:11}}>{ph.status}</div>
                    <div>{ph.from} → {ph.to||"Present"}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Planned vacations */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">☀ Planned Vacations</div>
              {(c.plannedVacations||[]).length===0&&<div style={{fontSize:12,color:C.dim}}>No planned vacations</div>}
              {(c.plannedVacations||[]).map((v,i)=>{
                const days=Math.round((new Date(v.to)-new Date(v.from))/(1000*60*60*24))+1;
                return (
                  <div key={i} className="flex items-center gap-2.5 px-3 py-2 bg-[#0e1c38] border border-[#14213d] rounded-lg mb-1.5 text-[12px] text-[#c4d4ee]">
                    <span style={{fontSize:16}}>🏖</span>
                    <div style={{flex:1}}>
                      <div>{v.label||"Vacation"}</div>
                      <div className="text-[11px] text-[#6889b4]">{v.from} → {v.to} ({days} days)</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Resume / CV */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">📄 Resume &amp; Profile</div>
              {c.resume?(
                <div style={{background:"#0e1c38",border:"1px solid #14213d",borderRadius:8,padding:14}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                    <span style={{fontSize:13,fontWeight:600,color:"#c4d4ee",display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:"#60a5fa"}}>📎</span> {c.resume.file}
                    </span>
                    <span style={{fontSize:10,color:"#4a6a96"}}>Uploaded {c.resume.uploadedAt}</span>
                  </div>
                  <div style={{fontSize:12,color:"#8fa4c8",lineHeight:1.6,padding:"8px 10px",background:"rgba(59,127,250,.04)",borderRadius:6,borderLeft:"3px solid #3b7ffa"}}>
                    {c.resume.summary}
                  </div>
                </div>
              ):<div style={{fontSize:12,color:C.dim}}>No resume uploaded</div>}
            </div>

            {/* Certifications */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">🏅 Certifications</div>
              {(c.certifications||[]).length===0&&<div style={{fontSize:12,color:C.dim}}>No certifications listed</div>}
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {(c.certifications||[]).map((cert,i)=>(
                  <span key={i} style={{padding:"4px 10px",borderRadius:5,fontSize:11,fontWeight:500,background:"rgba(168,85,247,.1)",color:"#a855f7",border:"1px solid rgba(168,85,247,.15)"}}>{cert}</span>
                ))}
              </div>
            </div>

            {/* Time Reports — hours logged per project */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">⏱ Reported Time by Project</div>
              {(c.timeReports||[]).length===0&&<div style={{fontSize:12,color:C.dim}}>No time reported yet</div>}
              {(c.timeReports||[]).map((tr,i)=>{
                const pct=tr.hoursPlanned>0?Math.round((tr.hoursLogged/tr.hoursPlanned)*100):0;
                const barColor=pct>=90?"#10c981":pct>=60?"#3b82f6":pct>=40?"#f59e0b":"#ef4444";
                return (
                  <div key={i} style={{background:"#0e1c38",border:"1px solid #14213d",borderRadius:8,padding:12,marginBottom:6}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:"#c4d4ee"}}>{tr.name}</div>
                        <div style={{fontSize:10,color:"#4a6a96",marginTop:2}}>{tr.period}</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:14,fontWeight:700,color:barColor}}>{tr.hoursLogged}h</div>
                        <div style={{fontSize:10,color:"#4a6a96"}}>of {tr.hoursPlanned}h planned</div>
                      </div>
                    </div>
                    <div style={{height:6,background:"#14213d",borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${Math.min(pct,100)}%`,background:barColor,borderRadius:3,transition:"width .3s"}}/>
                    </div>
                    <div style={{fontSize:10,color:"#4a6a96",marginTop:4,textAlign:"right"}}>{pct}% of planned hours</div>
                  </div>
                );
              })}
            </div>

            {/* Performance Summary */}
            <div className="mt-[22px]">
              <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">📊 Performance Summary</div>
              {(()=>{
                const margin=c.billRate&&c.costRate?Math.round(((c.billRate-c.costRate)/c.billRate)*100):0;
                const totalLogged=(c.timeReports||[]).reduce((sum,t)=>sum+t.hoursLogged,0);
                const totalPlanned=(c.timeReports||[]).reduce((sum,t)=>sum+t.hoursPlanned,0);
                const deliveryRate=totalPlanned>0?Math.round((totalLogged/totalPlanned)*100):0;
                const revenue=totalLogged*c.billRate;
                const cost=totalLogged*c.costRate;
                const profit=revenue-cost;
                return (
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                      <div className="rs-lbl">Gross Margin</div>
                      <div className={`rs-val ${margin>=40?"green":margin>=25?"yellow":"blue"}`}>{margin}%</div>
                      <div style={{fontSize:10,color:"#4a6a96",marginTop:2}}>{c.billRate} kr bill − {c.costRate} kr cost</div>
                    </div>
                    <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                      <div className="rs-lbl">Revenue Generated</div>
                      <div className="rs-val green">{revenue.toLocaleString()} kr</div>
                      <div style={{fontSize:10,color:"#4a6a96",marginTop:2}}>{totalLogged}h × {c.billRate} kr/hr</div>
                    </div>
                    <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                      <div className="rs-lbl">Profit Contribution</div>
                      <div className={`rs-val ${profit>0?"green":"yellow"}`}>{profit.toLocaleString()} kr</div>
                      <div style={{fontSize:10,color:"#4a6a96",marginTop:2}}>Revenue − Cost</div>
                    </div>
                    <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5">
                      <div className="rs-lbl">Delivery Rate</div>
                      <div className={`rs-val ${deliveryRate>=80?"green":deliveryRate>=50?"yellow":"blue"}`}>{deliveryRate}%</div>
                      <div style={{fontSize:10,color:"#4a6a96",marginTop:2}}>{totalLogged}h of {totalPlanned}h planned</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </SheetPrimitives.SheetBody>
        </SheetPrimitives.SheetContent>
      </SheetPrimitives.Sheet>
    );
  }

  return (
    <TooltipPrimitives.TooltipProvider delayDuration={200}>
    <div className="fade">
      <div className="pg-top">
        <div className="aud-shell-head">
          <div>
            <div className="pg-title">Resources</div>
            <div className="pg-sub">Consultant availability, assignments, and capacity alignment · Click any row for details</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            {isSuperAdmin && (
              <>
                <button className="wr-gen-btn" onClick={handleSync} disabled={syncing} style={{fontSize:12,padding:"7px 16px"}}>
                  {syncing ? "Syncing…" : "↻ Sync Tracker"}
                </button>
                <button className="wr-gen-btn" onClick={async()=>{setSyncing(true);try{const r=await apiClient.enrichConsultantsFromMonday();showToast?.(`Enriched ${r.matched} from Monday`,"success");const f=await apiClient.getConsultants();setConsultants(f.map(mapConsultant));}catch(e){showToast?.(e.message||"Enrich failed","error");}finally{setSyncing(false);}}} disabled={syncing} style={{fontSize:12,padding:"7px 16px"}}>
                  {syncing ? "…" : "↻ Enrich Monday"}
                </button>
              </>
            )}
            <button className="wr-gen-btn aud-cta-btn" onClick={()=>setShowNewModal(true)} style={{fontSize:12,padding:"7px 16px"}}>
              + New Consultant
            </button>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{display:"flex",gap:0,borderBottom:"2px solid #14213d",margin:"0 24px 16px"}}>
        {[["resources","Resources"],["intelligence","Resource Intelligence"]].map(([id,lbl])=>(
          <button key={id} onClick={()=>setTab(id)} style={{padding:"10px 20px",fontSize:13,fontWeight:600,cursor:"pointer",background:"none",border:"none",fontFamily:"inherit",color:tab===id?"#3b7ffa":"#8fa4c8",borderBottom:tab===id?"2px solid #3b7ffa":"2px solid transparent",marginBottom:-2}}>{lbl}</button>
        ))}
      </div>

      {tab === "resources" ? (
      <div className="pg-body">
        <div className="krow">
          <KpiCard label="Total Consultants" value={totalC} sub={`${fulltime.length} FT · ${talentHub.length} TH${interns.length ? ` · ${interns.length} Intern` : ""}`}/>
          <KpiCard label="Currently Deployed" value={deployed} sub={totalC > 0 ? `${Math.round((deployed/totalC)*100)}% of team` : "—"}/>
          <KpiCard label="Bench / Partial" value={bench} accent={bench>0?C.purple:C.green} sub={`${available.length} available`}/>
          <KpiCard label="Rolling Off (30d)" value={rollingOff} accent={rollingOff>0?C.yellow:C.green}/>
        </div>
        <div className="fbar">
          <input className="finput" placeholder="Search consultants or skills…" value={search} onChange={e=>setSearch(e.target.value)} style={{width:260}}/>
          <select className="fsel" value={statusF} onChange={e=>setStatusF(e.target.value)}>
            {["All","Deployed","Bench","Partial"].map(s=><option key={s}>{s}</option>)}
          </select>
          <select className="fsel" value={empTypeF} onChange={e=>setEmpTypeF(e.target.value)}>
            <option value="All">All Types</option>
            {["Full Time","Talent Hub","Intern"].map(t=><option key={t}>{t}</option>)}
          </select>
          <div className="finfo"><strong>{rows.length}</strong> consultant{rows.length!==1?"s":""}</div>
        </div>
        {/* Grouped resource tables */}
        {sections.map(section => (
          <div key={section.key} style={{marginBottom:24}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,padding:"8px 0",borderBottom:`2px solid ${section.accent}33`}}>
              <div style={{width:4,height:20,borderRadius:2,background:section.accent}}/>
              <span style={{fontSize:13,fontWeight:700,color:"#dce8f8"}}>{section.title}</span>
              <span style={{fontSize:11,color:"#8fa4c8",fontWeight:400}}>{section.data.length} consultant{section.data.length!==1?"s":""}</span>
            </div>
            <div className="tbl-wrap" style={{overflowX:"auto"}}>
              <table className="tbl" style={{minWidth:900}}>
                <thead style={{position:"sticky",top:0,zIndex:2}}>
                  <tr>
                    <Th col="name">Name</Th>
                    <th>Skills</th>
                    <th>Customer</th>
                    <Th col="utilizationPct">Utilisation</Th>
                    <th>Availability</th>
                    <Th col="billRate">Rate</Th>
                    <th>Vacation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {section.data.map(c=>{
                    const ss=statusStyle[c.status]||statusStyle.Deployed;
                    const ext = (c.currentProjects||[]).filter(p => p.name !== 'Rainocode Internal' && p.name !== 'Raincode Internal');
                    const isIntern = ["Intern","INTERN","intern"].includes(c.employmentType);
                    const vacLeft = (c.vacationDaysTotal||25) - (c.vacationDaysUsed||0);
                    return (
                      <tr key={c.consultantId||c.id} className="trow cursor-pointer transition-colors hover:!bg-[#0e1c38]" onClick={()=>setSelectedResId(c.id)}>
                        {/* Name + avatar + role + status */}
                        <td style={{minWidth:160}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <Avt initials={getInit(c.name)} size={26}/>
                            <div>
                              <div style={{fontWeight:600,color:"#dce8f8",fontSize:13}}>{c.name}</div>
                              <div style={{fontSize:10,color:"#8fa4c8",marginTop:2,display:"flex",alignItems:"center",gap:4}}>
                                {c.role}
                                <span className="chip" style={{color:ss.fg,background:ss.bg,border:`1px solid ${ss.fg}22`,fontSize:9,padding:"0 5px",lineHeight:"16px"}}>{c.status}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        {/* Skills — max 3 tags */}
                        <td style={{minWidth:120}}>
                          <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                            {(c.skills||[]).slice(0,3).map(s=><span key={s} style={{padding:"1px 7px",borderRadius:4,fontSize:10,fontWeight:500,background:C.blueB,color:C.blue,border:`1px solid ${C.blue}22`}}>{s}</span>)}
                            {(c.skills||[]).length>3&&<span style={{fontSize:10,color:C.dim}}>+{c.skills.length-3}</span>}
                            {(c.skills||[]).length===0&&<span style={{fontSize:10,color:C.dim}}>—</span>}
                          </div>
                        </td>
                        {/* Customer (Project) — allocation bars by actual hours */}
                        <td style={{minWidth:160}}>
                          {(() => {
                            // Filter to projects with actual logged hours, exclude 0h
                            const withHours = ext.filter(p => (p.loggedHours || 0) > 0);
                            const totalHrs = withHours.reduce((s,p) => s + (p.loggedHours || 0), 0);
                            if (withHours.length === 0 && ext.length === 0) return <span style={{color:C.purple,fontWeight:500,fontSize:11}}>Unassigned</span>;
                            // If projects exist but no hours logged, show names only
                            const display = withHours.length > 0 ? withHours : ext;
                            const colors = ["#3b7ffa","#10c981","#f59e0b","#a855f7","#ef4444"];
                            return (
                              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                                {display.slice(0,3).map((p,pi) => {
                                  const hrs = Number(p.loggedHours) || 0;
                                  const alloc = totalHrs > 0 ? Math.round((hrs / totalHrs) * 100) : 0;
                                  const col = colors[pi % colors.length];
                                  return (
                                    <div key={p.trackerProjectId} style={{display:"flex",alignItems:"center",gap:6}}>
                                      <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontSize:10,color:"#dce8f8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.clientName || p.name}</div>
                                        {totalHrs > 0 && (
                                          <div style={{height:4,background:"#14213d",borderRadius:2,marginTop:2}}>
                                            <div style={{width:`${alloc}%`,height:"100%",background:col,borderRadius:2}}/>
                                          </div>
                                        )}
                                      </div>
                                      {totalHrs > 0 && <span style={{fontSize:9,color:"#8fa4c8",whiteSpace:"nowrap"}}>{Math.round(hrs)}h</span>}
                                    </div>
                                  );
                                })}
                                {display.length > 3 && <span style={{fontSize:9,color:C.dim}}>+{display.length-3} more</span>}
                              </div>
                            );
                          })()}
                        </td>
                        {/* Utilisation — dual: Current + YTD, interns show N/A */}
                        <td style={{minWidth:140}}>
                          {isIntern ? (
                            <span style={{fontSize:11,color:C.dim}}>N/A</span>
                          ) : (
                            <div>
                              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                                <span style={{fontSize:9,color:"#4a6a96",width:35}}>Now</span>
                                <div className="minibar" style={{flex:1}}><div className="minibar-fill" style={{width:`${Math.min(c.currentUtilPct||0,100)}%`,background:(c.currentUtilPct||0)>=80?C.green:(c.currentUtilPct||0)>=60?C.yellow:C.red}}/></div>
                                <span className="mono" style={{fontSize:10,minWidth:28,textAlign:"right",color:"#dce8f8"}}>{c.currentUtilPct||0}%</span>
                              </div>
                              <div style={{display:"flex",alignItems:"center",gap:5}}>
                                <span style={{fontSize:9,color:"#4a6a96",width:35}}>YTD</span>
                                <div className="minibar" style={{flex:1}}><div className="minibar-fill" style={{width:`${Math.min(c.utilizationPct||0,100)}%`,background:(c.utilizationPct||0)>=80?"#3b82f6":(c.utilizationPct||0)>=60?"#60a5fa":"#93c5fd"}}/></div>
                                <span className="mono" style={{fontSize:10,minWidth:28,textAlign:"right",color:"#8fa4c8"}}>{c.utilizationPct||0}%</span>
                              </div>
                            </div>
                          )}
                        </td>
                        {/* Availability */}
                        <td>
                          {c.availableFrom ?
                            <span style={{color:"#8fa4c8",fontSize:11}}>{typeof c.availableFrom==="string"?c.availableFrom.slice(0,10):c.availableFrom}</span>
                            : <span style={{color:C.dim,fontSize:11}}>—</span>}
                        </td>
                        {/* Rate — interns show N/A */}
                        <td>
                          {isIntern ? (
                            <span style={{fontSize:11,color:C.dim}}>N/A</span>
                          ) : (
                            <span className="mono" style={{color:"#dce8f8",whiteSpace:"nowrap",fontSize:12}}>{c.billRate||0} kr</span>
                          )}
                        </td>
                        {/* Vacation */}
                        <td>
                          <span style={{fontSize:11,color:vacLeft<=5?"#f59e0b":vacLeft<=0?"#ef4444":"#8fa4c8"}}>{vacLeft}d left</span>
                        </td>
                        {/* Actions */}
                        <td onClick={e=>e.stopPropagation()}>
                          <div style={{display:"flex",gap:4}}>
                            <button onClick={()=>setSelectedResId(c.id)} title="Edit" style={{background:"none",border:"1px solid #14213d",borderRadius:4,padding:"2px 6px",color:"#60a5fa",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>✎</button>
                            <button onClick={async()=>{if(!window.confirm(`Delete ${c.name}?`))return;try{await apiClient.deleteConsultant(c.id);const f=await apiClient.getConsultants();setConsultants(f.map(mapConsultant));showToast?.(`${c.name} deleted`,"success");}catch(e){showToast?.(e.message||"Delete failed","error");}}} title="Delete" style={{background:"none",border:"1px solid #14213d",borderRadius:4,padding:"2px 6px",color:"#ef4444",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {sections.length === 0 && <div style={{textAlign:"center",padding:"40px 0",color:C.dim}}>No consultants found. Click "Sync Tracker" to import.</div>}

        {/* Inactive consultants — retired or end-of-contract. Shown below
            the active tables so the PM can see the audit trail without
            cluttering KPIs. Clicking a row still opens the same drawer so
            the inactive-since date / reason can be edited (e.g. re-activate
            by clearing the date). Excluded from all utilisation, cost,
            revenue, and headcount calculations from the cutoff onward. */}
        {inactiveConsultants.length > 0 && (
          <div style={{marginTop: 32}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,padding:"8px 0",borderBottom:"2px solid rgba(245,158,11,.3)"}}>
              <div style={{width:4,height:20,borderRadius:2,background:"#f59e0b"}}/>
              <span style={{fontSize:13,fontWeight:700,color:"#dce8f8"}}>Inactive</span>
              <span style={{fontSize:11,color:"#8fa4c8",fontWeight:400}}>{inactiveConsultants.length} consultant{inactiveConsultants.length!==1?"s":""}</span>
              <span style={{fontSize:10,fontWeight:600,color:"#f59e0b",marginLeft:"auto",padding:"2px 8px",borderRadius:10,background:"rgba(245,158,11,.1)",border:"1px solid rgba(245,158,11,.3)"}}>
                ⚠ excluded from utilisation / cost / revenue / headcount from cutoff
              </span>
            </div>
            <div className="tbl-wrap" style={{overflowX:"auto"}}>
              <table className="tbl" style={{minWidth:900}}>
                <thead style={{position:"sticky",top:0,zIndex:2}}>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Employment</th>
                    <th>Inactive From</th>
                    <th>Reason</th>
                    <th>Joined</th>
                    <th>Bill Rate</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveConsultants.map(c => {
                    const empLabel = EMP_MAP[c.employmentType]?.label || c.employmentType || "—";
                    return (
                      <tr key={c.consultantId||c.id} className="trow cursor-pointer transition-colors hover:!bg-[#0e1c38]" onClick={()=>setSelectedResId(c.id)} style={{opacity:0.75}}>
                        <td style={{minWidth:160}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <Avt initials={getInit(c.name)} size={26}/>
                            <div>
                              <div style={{fontWeight:600,color:"#dce8f8",fontSize:13,textDecoration:"line-through",textDecorationColor:"rgba(245,158,11,.6)",textDecorationThickness:"1px"}}>{c.name}</div>
                              <div style={{fontSize:10,color:"#8fa4c8",marginTop:2}}>{c.role}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{fontSize:12,color:"#8fa4c8"}}>{c.role}</td>
                        <td style={{fontSize:12,color:"#8fa4c8"}}>{empLabel}</td>
                        <td style={{fontSize:12,color:"#f59e0b",fontWeight:600}}>{c.inactiveSince}</td>
                        <td style={{fontSize:11,color:"#cbd5e1",maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.inactiveReason || "—"}</td>
                        <td style={{fontSize:12,color:"#8fa4c8"}}>{c.joinDate || "—"}</td>
                        <td style={{fontSize:12,color:"#8fa4c8"}}>{c.billRate ? `${c.billRate} kr` : "—"}</td>
                        <td onClick={e=>e.stopPropagation()}>
                          <div style={{display:"flex",gap:4}}>
                            <button onClick={()=>setSelectedResId(c.id)} title="Edit / Re-activate"
                              style={{background:"none",border:"1px solid #14213d",borderRadius:4,padding:"2px 8px",color:"#60a5fa",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
                              ✎ Edit
                            </button>
                            <button
                              onClick={async()=>{
                                if(!window.confirm(`Re-activate ${c.name}? They'll start contributing to all calculations again.`))return;
                                try {
                                  await apiClient.updateConsultant(c.id, { inactiveSince: null, inactiveReason: null });
                                  const fresh = await apiClient.getConsultants();
                                  setConsultants(fresh.map(mapConsultant));
                                  showToast?.(`${c.name} re-activated`, "success");
                                } catch (e) {
                                  showToast?.(e.message || "Failed to re-activate", "error");
                                }
                              }}
                              title="Re-activate — clears inactiveSince"
                              style={{background:"none",border:"1px solid rgba(16,201,129,.3)",borderRadius:4,padding:"2px 8px",color:"#10c981",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
                              ↻ Re-activate
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Monthly Utilization Heatmap */}
        <div style={{marginTop:24}}>
          <div style={{fontSize:10.5,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:".1em",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span>◎</span> Monthly Utilization Heatmap
          </div>
          <div className="panel">
            <div className="heat-grid">
              <div className="heat-name" style={{fontWeight:600,color:C.text,fontSize:12,paddingLeft:4}}>Consultant</div>
              {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map(m=><div key={m} className="heat-name" style={{textAlign:"center",fontSize:10}}>{m}</div>)}
              {rows.map(c=>(
                <div key={c.consultantId}>
                  <div className="heat-name" style={{paddingLeft:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:11,cursor:"pointer"}} onClick={()=>setSelectedResId(c.id)}>{c.name}</div>
                  {["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"].map(m=>{
                    const util=c.monthlyUtil?c.monthlyUtil[m]||0:0;
                    const heat=util===0?"#2d3748":util<40?"#93c5fd":util<70?"#60a5fa":util<90?"#3b82f6":"#1e40af";
                    return <div key={m} className="heat-cell" style={{background:heat,color:"#fff"}}>{util}%</div>;
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Demand vs Supply */}
        <div style={{marginTop:24}}>
          <div style={{fontSize:10.5,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:".1em",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span>◎</span> Demand vs Supply — Unmatched Pipeline Requirements
          </div>
          <div className="panel" style={{padding:0}}>
            {demandMatches.length===0?(
              <div style={{padding:20,textAlign:"center",color:C.dim,fontSize:13}}>All pipeline deals have assigned consultants</div>
            ):(
              demandMatches.map(({deal,match})=>(
                <div key={deal.dealId} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 18px",borderBottom:`1px solid ${C.border}`}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,color:C.text}}>{deal.companyName} — {deal.opportunityName}</div>
                    <div style={{fontSize:11,color:C.sub,marginTop:3}}>Needs: <strong>{deal.requiredRoleOrSkill}</strong> · Stage: {deal.stage} · Close: {deal.expectedCloseDate}</div>
                  </div>
                  <div style={{textAlign:"right",marginLeft:16}}>
                    {match?(
                      <div>
                        <span style={{padding:"3px 8px",borderRadius:4,fontSize:11,fontWeight:600,background:C.greenB,color:C.green,border:`1px solid ${C.green}22`}}>Match: {match.name}</span>
                        <div style={{fontSize:10,color:C.dim,marginTop:3}}>{match.role} · {match.status}</div>
                      </div>
                    ):(
                      <span style={{padding:"3px 8px",borderRadius:4,fontSize:11,fontWeight:600,background:C.redB,color:C.red,border:`1px solid ${C.red}22`}}>No match available</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      ) : (
        /* Resource Intelligence Tab */
        <ResourceIntelligenceTab consultants={activeConsultants} showToast={showToast} KpiCard={KpiCard} C={C}/>
      )}

      {/* Detail drawer — called as a function (not <ResDrawer/>) so the parent
          re-render doesn't remount it and reset uncontrolled inputs mid-edit. */}
      {ResDrawer()}

      {/* New Consultant Modal — Phase 5a: aud-modal-* → shadcn <Dialog>. */}
      <DialogPrimitives.Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogPrimitives.DialogContent className="max-w-[560px]">
          <DialogPrimitives.DialogHeader>
            <DialogPrimitives.DialogTitle>Add New Consultant</DialogPrimitives.DialogTitle>
          </DialogPrimitives.DialogHeader>

          <div className="flex flex-col gap-3">
            <FieldGroup>
              <FieldLabel required>Name</FieldLabel>
              <Input placeholder="e.g. Ali Turabi" value={newForm.name} onChange={e=>setNewForm(p=>({...p,name:e.target.value}))} autoFocus />
            </FieldGroup>

            <div className="grid grid-cols-2 gap-3">
              <FieldGroup>
                <FieldLabel>Role</FieldLabel>
                <Input placeholder="e.g. Full Stack Developer" value={newForm.role} onChange={e=>setNewForm(p=>({...p,role:e.target.value}))} />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Employment Type</FieldLabel>
                <Select value={newForm.employmentType} onChange={e=>setNewForm(p=>({...p,employmentType:e.target.value}))}>
                  <option value="FULL_TIME">Full Time</option>
                  <option value="TALENT_HUB">Talent Hub</option>
                </Select>
              </FieldGroup>
            </div>

            <FieldGroup>
              <FieldLabel>Skills (comma-separated)</FieldLabel>
              <Input placeholder="React, Node.js, AWS, DevOps" value={newForm.skills} onChange={e=>setNewForm(p=>({...p,skills:e.target.value}))} />
            </FieldGroup>

            <div className="grid grid-cols-2 gap-3">
              <FieldGroup>
                <FieldLabel>Bill Rate (kr/hr)</FieldLabel>
                <Input type="number" placeholder="600" value={newForm.billRate} onChange={e=>setNewForm(p=>({...p,billRate:e.target.value}))} />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Cost Rate (kr/hr)</FieldLabel>
                <Input type="number" placeholder="300" value={newForm.costRate} onChange={e=>setNewForm(p=>({...p,costRate:e.target.value}))} />
              </FieldGroup>
            </div>

            <FieldGroup>
              <FieldLabel>Monthly Salary (kr)</FieldLabel>
              <Input type="number" placeholder="45000" value={newForm.salary} onChange={e=>setNewForm(p=>({...p,salary:e.target.value}))} />
            </FieldGroup>
          </div>

          <DialogPrimitives.DialogFooter>
            <Button variant="secondary" onClick={()=>setShowNewModal(false)}>Cancel</Button>
            <Button onClick={handleCreateConsultant} disabled={savingNew||!newForm.name.trim()}>
              {savingNew?"Creating…":"Create Consultant"}
            </Button>
          </DialogPrimitives.DialogFooter>
        </DialogPrimitives.DialogContent>
      </DialogPrimitives.Dialog>
    </div>
    </TooltipPrimitives.TooltipProvider>
  );
}
